import { z } from 'zod'
import { runtimeConfig } from '@/shared/config'
import {
  lastSessionKind,
  refreshSession,
  rememberSessionKind,
  type SessionKind,
  sessionExpired,
} from './client'

// Своей формы логина/логаута у telemost НЕТ. Вход обеих аудиторий — единый
// экран хаба (`plan-customer/22`): сотрудник уходит оттуда в Authentik
// (cookie stit_session), заказчик вводит логин и пароль (cookie stit_customer).
// Продукт при 401 не знает, кто перед ним, поэтому ведёт именно туда, а не на
// id.stit.tech. Логаут — у issuer'а своего контура.
// Identity читаем у СВОЕГО backend'а (/api/auth/me, same-origin): хаб отдаёт
// CORS-заголовки только своему фронту, и cross-origin /me из браузера не
// прочитать. Ту же cookie backend верифицирует локально по JWKS.
// Гостевой контур (guest_token/lobby/guest_session) остаётся ЛОКАЛЬНЫМ и этой
// линии auth не касается — гость не hub-принципал.

const idBase = runtimeConfig.ID_BASE_URL
const hubApiBase = runtimeConfig.HUB_API_BASE_URL

// Session — проекция /api/auth/me в форму, нужную UI телемоста: идентификатор
// (sub), логин, отображаемое имя и hub-роль. role — из hub-claim (admin|client|…);
// UI-гейтов по роли у телемоста нет (все права считает бэк и отдаёт в DTO
// встречи), поле держим для полноты. kind — контур сессии: он разводит
// сотрудника и заказчика по разным раскладкам и правам.
export const SessionSchema = z.object({
  sub: z.string(),
  username: z.string().optional(),
  name: z.string().optional(),
  role: z.string().optional(),
  kind: z.enum(['employee', 'customer']).default('employee'),
  // Компания заказчика (claim customer-сессии); у сотрудника пусто.
  companyId: z.string().optional(),
  companyName: z.string().optional(),
  // Продукты, выданные аккаунту заказчика: свитчер показывает только их.
  products: z.array(z.string()).optional(),
})

export type Session = z.infer<typeof SessionSchema>

// Wire /api/auth/me (huma v2) в data-обёртке. kind появляется вместе с
// customer-контуром бэка; без него сессия — сотрудничья, как было.
const MeSchema = z.object({
  sub: z.string(),
  username: z.string().optional(),
  name: z.string().optional(),
  email: z.string().optional(),
  role: z.string().optional(),
  kind: z.enum(['employee', 'customer']).optional(),
  company_id: z.string().optional(),
  company_name: z.string().optional(),
  products: z.array(z.string()).optional(),
})

function unwrapData(json: unknown): unknown {
  return json && typeof json === 'object' && 'data' in json ? json.data : json
}

// Пропускаем только внутренний absolute-path ("/meetings/1"): не "//evil"
// (protocol-relative), не "\..\", без CRLF — защита от open-redirect.
// Бэк санитайзит rd повторно — тут двойная страховка.
export function sanitizeInternalPath(raw: string | null | undefined): string | undefined {
  if (raw?.[0] !== '/' || raw.startsWith('//') || /[\\\r\n]/.test(raw)) return undefined
  return raw
}

// rd для BFF id.stit.tech — всегда absolute URL (чужой origin должен знать, на
// какой origin вернуть). Уже-абсолютный URL берём как есть (без URL-нормализации,
// чтобы не появлялся хвостовой '/'); относительный путь резолвим через origin.
function absoluteReturnTo(returnTo: string): string {
  if (/^https?:\/\//.test(returnTo)) return returnTo
  try {
    return new URL(returnTo, window.location.origin).href
  } catch {
    return window.location.origin
  }
}

// Единая точка входа экосистемы. notice=expired отличает «сессия истекла» от
// «ты сюда впервые» — экран входа объясняет, почему человека выкинуло.
export function loginUrl(returnTo: string = window.location.href): string {
  const rd = encodeURIComponent(absoluteReturnTo(returnTo))
  return `${runtimeConfig.HUB_LOGIN_URL}?rd=${rd}${sessionExpired() ? '&notice=expired' : ''}`
}

// Реальная браузерная навигация (не router-redirect): уходим на чужой origin
// hub.stit.tech. По умолчанию возвращаемся на текущий полный URL.
export function redirectToLogin(returnTo: string = window.location.href): void {
  window.location.assign(loginUrl(returnTo))
}

// Своей формы логина нет, поэтому «нет сессии» на странице сотрудника = уводим на
// SSO сразу. Ровно один автопереход за вкладку: если вернулись с id.stit.tech и
// сессии всё равно нет (cookie не встала, доступ отозван), второй прыжок закольцевал
// бы продукт с SSO — вместо этого false, и вызывающий показывает экран входа. Флаг
// живёт в sessionStorage: переживает навигацию на чужой origin, умирает со вкладкой.
// Гостевых маршрутов это не касается — гость не hub-принципал и живёт без сессии.
const LOGIN_ATTEMPT_KEY = 'auth:login-attempt'

export function autoRedirectToLogin(returnTo?: string): boolean {
  // Приватные режимы/выключенный storage кидают на доступе — без памяти о попытке
  // автопереход неотличим от цикла, поэтому отдаём решение экрану.
  try {
    if (sessionStorage.getItem(LOGIN_ATTEMPT_KEY)) return false
    sessionStorage.setItem(LOGIN_ATTEMPT_KEY, '1')
  } catch {
    return false
  }
  redirectToLogin(returnTo)
  return true
}

// Уже ходили на SSO в этой вкладке — значит вернулись без сессии.
export function hasLoginAttempt(): boolean {
  try {
    return sessionStorage.getItem(LOGIN_ATTEMPT_KEY) !== null
  } catch {
    return true
  }
}

export function clearLoginAttempt(): void {
  try {
    sessionStorage.removeItem(LOGIN_ATTEMPT_KEY)
  } catch {
    // storage недоступен — нечего чистить
  }
}

// Выход гасит сессию у её issuer'а — контуры независимы, и общего логаута нет.
// Сотрудник: id.stit.tech/auth/logout. Контракт хаба — POST (state-changing;
// GET → 405 и уязвим к prefetch/CSRF-логауту), поэтому не location.assign, а
// form-POST навигация: same-site POST шлёт hub-cookie без CORS, браузер сам
// следует 302 на ?rd=.
// Заказчик: company-backend знает только XHR-ручку (/auth/customer/logout,
// cross-origin с credentials), редиректа с ?rd= у неё нет — гасим сессию
// запросом и уводим на единый вход сами. Сеть отвалилась — уводим всё равно:
// cookie переживёт, но человек не должен залипнуть в приложении.
export function redirectToLogout(returnTo: string = window.location.origin): void {
  if (lastSessionKind() === 'customer') {
    void fetch(`${hubApiBase}/auth/customer/logout`, {
      method: 'POST',
      credentials: 'include',
    })
      .catch(() => undefined)
      .finally(() => {
        rememberSessionKind(null)
        window.location.assign(loginUrl(returnTo))
      })
    return
  }
  const form = document.createElement('form')
  form.method = 'post'
  form.action = `${idBase}/auth/logout?rd=${encodeURIComponent(absoluteReturnTo(returnTo))}`
  document.body.appendChild(form)
  form.submit()
}

// null = нет hub-сессии. 401 / сетевые сбои / HTML-фолбэк dev-сервера трактуем
// как «не сотрудник» (а не как ошибку) — soft-семантика важна: гостевой контур
// живёт без hub-сессии, и наличие сессии лишь разветвляет UI (сотрудник/гость),
// а не форсит редирект на SSO. Force-redirect делают гейты страниц сотрудника.
export async function fetchMe(): Promise<Session | null> {
  const probe = (): Promise<Response> =>
    fetch(`${runtimeConfig.API_BASE_URL}/auth/me`, {
      credentials: 'include',
      headers: { accept: 'application/json' },
    })
  try {
    // 401 здесь двусмыслен: и гость, и сотрудник с протухшим access отвечают им
    // одинаково, а различает их только хаб — поэтому проба, как и остальной API,
    // сначала пробует продлить сессию. Гостю refresh откажет, и soft-семантика
    // (null = не сотрудник) остаётся прежней.
    let res = await probe()
    if (res.status === 401 && (await refreshSession())) res = await probe()
    if (!res.ok) return null
    if (!(res.headers.get('content-type') ?? '').includes('application/json')) return null
    const me = MeSchema.parse(unwrapData(await res.json()))
    // Контур, который назвал бэк, — единственный достоверный источник: по нему
    // клиент решает, в какой issuer идти за продлением следующей 401.
    const kind: SessionKind = me.kind ?? 'employee'
    rememberSessionKind(kind)
    return {
      sub: me.sub,
      kind,
      ...(me.username ? { username: me.username } : {}),
      ...(me.name ? { name: me.name } : {}),
      ...(me.role ? { role: me.role } : {}),
      ...(me.company_id ? { companyId: me.company_id } : {}),
      ...(me.company_name ? { companyName: me.company_name } : {}),
      ...(me.products ? { products: me.products } : {}),
    }
  } catch {
    return null
  }
}
